// GtCandidateBulk — bulk actions for people ticked on the Candidates table:
// tag and untag, add to a role, invite to an interview with Nathan, mark not
// looking, and merge two duplicates. Every modal reports what happened to each
// person. Nothing here emails a candidate except an interview invite, and only
// when the box for it is ticked.
import { useState, useEffect } from 'react';
import { GtSelect } from './GtControls';
import GtNoticeModal from './GtNoticeModal';
import GtBulkNoticeModal from './GtBulkNoticeModal';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
const MAX_INVITES = 200;

function useOpenRoles(authFetch) {
  const [roles, setRoles] = useState(null);
  useEffect(() => {
    authFetch('/api/roles').then(r => {
      const list = Array.isArray(r) ? r : (r.roles || []);
      const open = list.filter(x => ['filled', 'closed'].indexOf(x.status) === -1);
      open.sort((a, b) => String(a.title).localeCompare(String(b.title)));
      setRoles(open);
    }).catch(() => setRoles([]));
  }, [authFetch]);
  return roles;
}
const roleOptions = (roles) => (roles || []).map(r => ({ value: r.id, label: r.title + (r.client_name ? ' · ' + r.client_name : '') }));

function Shell({ title, busy, onClose, foot, extra, children }) {
  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">{title}</div>
        <div className="gtc-modal-body">{children}</div>
        <div className="gtc-modal-foot">{foot}</div>
        {extra}
      </div>
    </div>
  );
}

function Foot({ done, busy, onClose, onGo, disabled, label, busyLabel }) {
  if (done) return <button type="button" className="gtc-primary" onClick={onClose}>Done</button>;
  return (
    <>
      <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
      <button type="button" className="gtc-primary" disabled={busy || disabled} onClick={onGo}>{busy ? busyLabel : label}</button>
    </>
  );
}

// ---- Tag and untag ----
export function BulkTagModal({ candidateIds, tags, authFetch, flash, onClose, onDone }) {
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const add = Object.keys(pick).filter(k => pick[k] === 'add').map(Number);
  const remove = Object.keys(pick).filter(k => pick[k] === 'remove').map(Number);
  const n = candidateIds.length;
  const toggle = (id, v) => setPick(p => { const next = { ...p }; if (next[id] === v) delete next[id]; else next[id] = v; return next; });

  async function go() {
    setBusy(true);
    try {
      const r = await authFetch('/api/candidate-bulk/tags', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ candidate_ids: candidateIds, add, remove }) });
      setResult(r);
      if (onDone) onDone();
    } catch (e) { flash(e.message); }
    setBusy(false);
  }

  return (
    <Shell title={'Tag ' + plural(n, 'person', 'people')} busy={busy} onClose={onClose}
      foot={<Foot done={!!result} busy={busy} onClose={onClose} onGo={go} disabled={!add.length && !remove.length}
        label={'Apply to ' + n} busyLabel="Saving…" />}>
      {result ? (
        <div className="gtnt-banner gtnt-banner--green">
          <b>Tags updated for {plural(result.people, 'person', 'people')}.</b>
          {' '}{result.added} added, {result.removed} removed. Anyone who already had a tag was left as they were.
        </div>
      ) : tags.length === 0 ? <div className="gtr-empty-sm">No tags yet. Create one with the Tags button first.</div> : (
        <>
          <p className="gtbn-help">Choose tags to add or remove. Tags you leave alone are not changed.</p>
          <div className="gtbk-list">
            {tags.map(t => (
              <div key={t.id} className="gtbk-row">
                <span className="gtc-tagchip" style={{ background: t.colour }}>{t.name}</span>
                <span className="gtbk-seg">
                  <button type="button" className={pick[t.id] === 'add' ? 'gtc-primary' : 'gtc-btn-ghost'} onClick={() => toggle(t.id, 'add')}>Add</button>
                  <button type="button" className={pick[t.id] === 'remove' ? 'gtc-danger' : 'gtc-btn-ghost'} onClick={() => toggle(t.id, 'remove')}>Remove</button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </Shell>
  );
}

// ---- Add to a role ----
export function BulkRoleModal({ candidateIds, authFetch, flash, onClose, onDone }) {
  const roles = useOpenRoles(authFetch);
  const [roleId, setRoleId] = useState('');
  const [stages, setStages] = useState([]);
  const [stageId, setStageId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [noticeIds, setNoticeIds] = useState(null);
  const [bulkNoticeIds, setBulkNoticeIds] = useState(null);
  const n = candidateIds.length;

  useEffect(() => {
    if (!roleId) { setStages([]); setStageId(''); return; }
    authFetch('/api/roles/' + roleId + '/stages').then(r => {
      const st = (r.stages || []).filter(s => s.outcome === 'open' && s.side !== 'client');
      setStages(st);
      const entry = st.find(s => s.key === 'sourced') || st.find(s => s.is_entry) || st[0];
      setStageId(entry ? entry.id : '');
    }).catch(() => setStages([]));
  }, [authFetch, roleId]);

  async function go() {
    setBusy(true);
    try {
      const r = await authFetch('/api/candidate-bulk/to-role', {
        method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({ candidate_ids: candidateIds, role_id: Number(roleId), stage_id: stageId ? Number(stageId) : null }),
      });
      setResult(r);
      if (onDone) onDone();
      const ids = (r.notices || []).map(x => x.id);
      if (ids.length === 1) setNoticeIds(ids);
      else if (ids.length > 1) setBulkNoticeIds(ids);
    } catch (e) { flash(e.message); }
    setBusy(false);
  }

  return (
    <Shell title={'Add ' + plural(n, 'person', 'people') + ' to a role'} busy={busy} onClose={onClose}
      foot={<Foot done={!!result} busy={busy} onClose={onClose} onGo={go} disabled={!roleId} label={'Add ' + n} busyLabel="Adding…" />}
      extra={<>
        {noticeIds && <GtNoticeModal ids={noticeIds} authFetch={authFetch} onClose={() => setNoticeIds(null)} />}
        {bulkNoticeIds && <GtBulkNoticeModal ids={bulkNoticeIds} authFetch={authFetch} onClose={() => setBulkNoticeIds(null)} />}
      </>}>
      {result ? (
        <div className={'gtnt-banner ' + (result.failed ? 'gtnt-banner--amber' : 'gtnt-banner--green')}>
          <b>{result.added} added to {result.role.title}.</b>
          {result.already_on_role > 0 && ' ' + result.already_on_role + ' were already on it and were left where they are.'}
          {result.failed > 0 && ' ' + result.failed + ' could not be added.'}
          <ul className="gtnt-blocked">
            {result.results.map(x => (
              <li key={x.candidate_id}><b>{x.name || 'Someone'}</b> {x.result === 'added' ? 'added at ' + (x.stage || 'the first stage')
                : x.result === 'already_on_role' ? 'already on this role' + (x.stage ? ', at ' + x.stage : '')
                : x.message}</li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          <p className="gtbn-help">They join the role's pipeline at the stage you pick. Nobody is emailed unless you choose to.</p>
          <div className="gtc-field"><span>Role</span>
            {roles === null ? <div className="gtr-empty-sm">Loading roles…</div> : roles.length === 0
              ? <div className="gtr-empty-sm">There are no open roles.</div>
              : <GtSelect value={roleId} onChange={setRoleId} options={roleOptions(roles)} placeholder="Choose a live role" />}
          </div>
          {roleId && (
            <div className="gtc-field"><span>Stage</span>
              <GtSelect value={stageId} onChange={setStageId} placeholder="Choose a stage" options={stages.map(s => ({ value: s.id, label: s.label }))} />
            </div>
          )}
        </>
      )}
    </Shell>
  );
}

// ---- Invite to an interview with Nathan ----
const SKIP_REASON = {
  already_invited: 'already has an open invite for this role',
  consent_withdrawn: 'has withdrawn consent, so was not invited',
  closed: 'has a closed record',
  not_found: 'could not be found',
};

export function BulkInterviewModal({ candidateIds, authFetch, flash, onClose, onDone }) {
  const roles = useOpenRoles(authFetch);
  const [roleId, setRoleId] = useState('');
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const n = candidateIds.length;
  const tooMany = n > MAX_INVITES;

  async function go() {
    setBusy(true);
    try {
      const r = await authFetch('/api/interviews/invite', {
        method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({ kind: 'role', id: Number(roleId), candidate_ids: candidateIds, email }),
      });
      setResult(r);
      if (onDone) onDone();
    } catch (e) { flash(e.message); }
    setBusy(false);
  }

  const created = (result && result.created) || [];
  const skipped = (result && result.skipped) || [];
  const notEmailed = created.filter(x => email && !x.emailed).length;

  return (
    <Shell title={'Invite ' + plural(n, 'person', 'people') + ' to an interview'} busy={busy} onClose={onClose}
      foot={<Foot done={!!result} busy={busy} onClose={onClose} onGo={go} disabled={!roleId || tooMany}
        label={'Invite ' + n} busyLabel="Inviting…" />}>
      {result ? (
        <div className={'gtnt-banner ' + (skipped.length || notEmailed ? 'gtnt-banner--amber' : 'gtnt-banner--green')}>
          <b>{plural(created.length, 'person', 'people')} invited.</b>
          {email ? (notEmailed ? ' ' + notEmailed + ' could not be emailed: send their link from the Interviews screen.' : ' Each one has been emailed their link.') : ' Nobody was emailed.'}
          {skipped.length > 0 && (
            <ul className="gtnt-blocked">
              {skipped.map(x => <li key={x.candidate_id}><b>{x.name || 'Someone'}</b> {SKIP_REASON[x.reason] || x.reason}</li>)}
            </ul>
          )}
        </div>
      ) : (
        <>
          <p className="gtbn-help">Each person gets their own interview with Nathan for the role you pick. The role's interview plan must be approved first. Anyone already invited for that role is skipped.</p>
          {tooMany && <div className="gtnt-banner gtnt-banner--amber">Invite up to {MAX_INVITES} people at a time. Untick some and try again.</div>}
          <div className="gtc-field"><span>Role</span>
            {roles === null ? <div className="gtr-empty-sm">Loading roles…</div> : roles.length === 0
              ? <div className="gtr-empty-sm">There are no open roles.</div>
              : <GtSelect value={roleId} onChange={setRoleId} options={roleOptions(roles)} placeholder="Choose a live role" />}
          </div>
          <label className="gtbk-check">
            <input type="checkbox" checked={email} onChange={e => setEmail(e.target.checked)} />
            <span>Email each person their interview link</span>
          </label>
        </>
      )}
    </Shell>
  );
}

// ---- Not looking, and undo ----
export function BulkLookingModal({ candidateIds, authFetch, flash, onClose, onDone }) {
  const [mode, setMode] = useState('not');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const n = candidateIds.length;
  const undo = mode === 'again';

  async function go() {
    setBusy(true);
    try {
      const r = await authFetch('/api/candidate-bulk/not-looking', {
        method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({ candidate_ids: candidateIds, undo, note: undo ? '' : note }),
      });
      setResult(r);
      if (onDone) onDone();
    } catch (e) { flash(e.message); }
    setBusy(false);
  }

  return (
    <Shell title={'Looking status for ' + plural(n, 'person', 'people')} busy={busy} onClose={onClose}
      foot={<Foot done={!!result} busy={busy} onClose={onClose} onGo={go} disabled={false}
        label={undo ? 'Mark ' + n + ' as looking' : 'Mark ' + n + ' not looking'} busyLabel="Saving…" />}>
      {result ? (
        <div className="gtnt-banner gtnt-banner--green">
          <b>{result.done} {result.undo ? 'marked as looking again' : 'marked not looking'}.</b>
          {result.already > 0 && ' ' + result.already + (result.undo ? ' were not marked not looking.' : ' were already marked.')}
          {result.other > 0 && ' ' + result.other + ' have unsubscribed or bounced, and were left as they are.'}
          {result.no_email > 0 && ' ' + result.no_email + ' have no email address, so nothing was recorded for them.'}
        </div>
      ) : (
        <>
          <div className="gtbk-list">
            <button type="button" className={'gtbk-choice' + (mode === 'not' ? ' gtbk-choice--on' : '')} onClick={() => setMode('not')}>
              <b>Not looking right now</b>
              <span>They stay in the pool, on every bench and role. Outreach skips them until you mark them as looking again.</span>
            </button>
            <button type="button" className={'gtbk-choice' + (mode === 'again' ? ' gtbk-choice--on' : '')} onClick={() => setMode('again')}>
              <b>Looking again</b>
              <span>Clears not looking. An unsubscribe is never changed here: only the person can undo that.</span>
            </button>
          </div>
          {!undo && (
            <label className="gtc-field"><span>Note (optional)</span>
              <input value={note} maxLength={500} placeholder="e.g. Happy where they are, try again in spring" onChange={e => setNote(e.target.value)} />
            </label>
          )}
        </>
      )}
    </Shell>
  );
}

// ---- Merge two duplicates ----
export function MergeModal({ ids, authFetch, flash, onClose, onDone }) {
  const [people, setPeople] = useState(null);
  const [keep, setKeep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    authFetch('/api/filter/candidates', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ tree: { or: ids.map(id => ({ field: 'id', op: 'eq', value: id })) }, limit: 2 }),
    }).then(r => {
      const rows = (r.rows || []).slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setPeople(rows);
      if (rows.length) setKeep(rows[0].id);
    }).catch(e => { flash(e.message); setPeople([]); });
  }, [authFetch, ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const kept = (people || []).find(p => p.id === keep);
  const other = (people || []).find(p => p.id !== keep);

  async function go() {
    if (!kept || !other) return;
    setBusy(true);
    try {
      const r = await authFetch('/api/candidates/' + kept.id + '/merge', {
        method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ duplicate_id: other.id }),
      });
      setResult(r);
      if (onDone) onDone();
    } catch (e) { flash(e.message === 'admin role required' ? 'Only an admin can merge records.' : e.message); }
    setBusy(false);
  }

  const nm = (p) => p.full_name || [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Unnamed';
  const added = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d) ? d.toLocaleDateString('en-GB') : ''; };

  return (
    <Shell title="Merge two records" busy={busy} onClose={onClose}
      foot={<Foot done={!!result} busy={busy} onClose={onClose} onGo={go} disabled={!kept || !other}
        label={kept ? 'Merge into ' + nm(kept) : 'Merge'} busyLabel="Merging…" />}>
      {result ? (
        <div className="gtnt-banner gtnt-banner--green">
          <b>Merged into {kept ? nm(kept) : 'the kept record'}.</b> Everything from the other record now sits on this one.
        </div>
      ) : people === null ? <div className="gtr-empty-sm">Loading…</div> : people.length !== 2 ? (
        <div className="gtr-empty-sm">Both records need to be open in the pool to merge them.</div>
      ) : (
        <>
          <p className="gtbn-help">Pick the record to keep. Notes, documents, tags, roles, benches, interviews, applications and shares move onto it, and its empty fields are filled from the other. The other record is then hidden. Where both were on the same role or bench, the kept record's place stays as it is.</p>
          <div className="gtbk-list">
            {people.map(p => (
              <button key={p.id} type="button" className={'gtbk-choice' + (keep === p.id ? ' gtbk-choice--on' : '')} onClick={() => setKeep(p.id)}>
                <b>{nm(p)} {keep === p.id ? '· keep' : ''}</b>
                <span>{[p.email, p.current_title, p.current_company].filter(Boolean).join(' · ') || 'No contact details'}</span>
                <span>{p.ref || '#' + p.id}{added(p.created_at) ? ' · added ' + added(p.created_at) : ''}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </Shell>
  );
}
